import { ThemeId, themeProfiles } from './themeProfiles';

const THEME_STORAGE_KEY = 'awkherd.theme';
const THEME_MODE_STORAGE_KEY = 'awkherd.theme.mode';

export type ThemeMode = 'manual' | 'auto';

export type ThemeTokens = {
  background: string;
  surface: string;
  surfaceMuted: string;
  text: string;
  textSecondary: string;
  textInverse: string;
  primary: string;
  primaryContrast: string;
  secondary: string;
  secondaryContrast: string;
  accent: string;
};

type ThemeChangeListener = (theme: ThemeId) => void;

const listeners = new Set<ThemeChangeListener>();

const CSS_TOKEN_MAP: Record<keyof ThemeTokens, string> = {
  background: '--color-background',
  surface: '--color-surface',
  surfaceMuted: '--color-surface-muted',
  text: '--color-text-primary',
  textSecondary: '--color-text-secondary',
  textInverse: '--color-text-inverse',
  primary: '--color-primary',
  primaryContrast: '--color-primary-contrast',
  secondary: '--color-secondary',
  secondaryContrast: '--color-secondary-contrast',
  accent: '--color-accent',
};

const setDocumentTheme = (theme: ThemeId | 'auto', colorScheme?: 'light' | 'dark') => {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.dataset.theme = theme;
  document.documentElement.style.setProperty(
    'color-scheme',
    colorScheme ?? (theme === 'dark' ? 'dark' : 'light'),
  );
};

const detectSystemPreference = (): ThemeId => {
  if (typeof window === 'undefined') {
    return 'normal';
  }

  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'normal';
};

export const loadStoredTheme = (): ThemeId => {
  if (typeof window === 'undefined') {
    return 'normal';
  }

  const stored = window.localStorage.getItem(THEME_STORAGE_KEY) as ThemeId | null;
  return stored && themeProfiles[stored] ? stored : detectSystemPreference();
};

export const persistTheme = (theme: ThemeId) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(THEME_STORAGE_KEY, theme);
};

export const loadThemeMode = (): ThemeMode => {
  if (typeof window === 'undefined') {
    return 'auto';
  }
  const stored = window.localStorage.getItem(THEME_MODE_STORAGE_KEY) as ThemeMode | null;
  if (stored === 'auto' || stored === 'manual') {
    return stored;
  }
  return 'auto';
};

export const persistThemeMode = (mode: ThemeMode) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
};

export const setTheme = (theme: ThemeId) => {
  persistTheme(theme);
  setDocumentTheme(theme);
  listeners.forEach((listener) => listener(theme));
};

export const subscribeToTheme = (listener: ThemeChangeListener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const initializeTheme = () => {
  const initialTheme = loadStoredTheme();
  setDocumentTheme(initialTheme);
  return initialTheme;
};

export const themeStorageKey = THEME_STORAGE_KEY;
export const themeModeStorageKey = THEME_MODE_STORAGE_KEY;

export const applyThemeTokens = (tokens: ThemeTokens) => {
  if (typeof document === 'undefined') {
    return;
  }

  Object.entries(tokens).forEach(([token, value]) => {
    const cssVariable = CSS_TOKEN_MAP[token as keyof ThemeTokens];
    if (cssVariable) {
      document.documentElement.style.setProperty(cssVariable, value);
    }
  });
};

export const updateDocumentTheme = (
  theme: ThemeId | 'auto',
  colorScheme?: 'light' | 'dark',
) => {
  setDocumentTheme(theme, colorScheme);
};
